import { defineStore } from 'pinia'
import localStorage from "../services/localStorage"
import router from '../router'
import http from '../services/http'
import { useAppStore } from "./app"
import { useNotificationStore } from "./notification"
import { computed, ref } from 'vue'
import profileImage from "../assets/img/guest.png"
import browser from '@/services/browser'
import { CLIENT_ERROR } from '@/utils/constants'

const AUTH_COOKIE = 'Cookie'
const AUTH_TOKEN = 'Token'
const DEFAULT_AUTH_PROVIDER = AUTH_COOKIE
const DEFAULT_THEME = 'light'
const DEFAULT_USER = 'guest'
const DEFAULT_LANG = 'en'

function getExpireDateFromSecs(secs) {
    var t = new Date()
    t.setSeconds(t.getSeconds() + secs)
    return t.valueOf()
}

function validExpireDate(valueOfDate) {
    return valueOfDate > Date.now()
}

function setup() {
    const redirectUrl = ref("")

    const displayName = ref(DEFAULT_USER)
    const language = ref(DEFAULT_LANG)
    const theme = ref(DEFAULT_THEME)
    const pictureUrl = ref("")
    const canChangePicture = ref(false)
    const canChangePassword = ref(false)

    const signedin = ref(false)
    const roles = ref([])

    const authProvider = ref(DEFAULT_AUTH_PROVIDER)
    const accessToken = ref(null)
    const tokenExpire = ref(0)
    const refreshToken = ref("")
    const refreshTokenExpire = ref(0)
    const profilePageUrl = ref("")

    const overrideHomepage = ref("")

    const locale = computed(() => language.value == 'th' ? 'th-TH' : 'en-US')

    async function init() {
        await getUserData()
        startHub()
    }

    function startHub() {
        if (signedin.value && process.env.VUE_APP_HUB_ENABLE == 'true') {
            const notificationStore = useNotificationStore()
            notificationStore.startHub()
        }
    }

    function signin() {
        redirectUrl.value = router.currentRoute.path
        router.replace('/signin')
    }

    function setToken(token) {
        if (token) {
            accessToken.value = token.token
            tokenExpire.value = token.tokenExpire
            refreshToken.value = token.refreshToken
            refreshTokenExpire.value = token.refreshTokenExpire

            localStorage.setItem("accessToken", token.token)
            localStorage.setItem("tokenExpire", getExpireDateFromSecs(token.tokenExpireSecs).toString())
            localStorage.setItem("refrehToken", token.refreshToken)
            localStorage.setItem("refreshTokenExpire", getExpireDateFromSecs(token.refreshTokenExpireSecs).toString())
        } else {
            accessToken.value = null
            tokenExpire.value = 0
            refreshToken.value = null
            refreshTokenExpire.value = 0

            localStorage.setItem("accessToken", null)
            localStorage.setItem("tokenExpire", '0')
            localStorage.setItem("refrehToken", null)
            localStorage.setItem("refreshTokenExpire", '0')
        }
    }

    async function getAccessToken() {
        if (authProvider.value == AUTH_TOKEN && accessToken.value != null) {
            if (!validExpireDate(tokenExpire.value)) {
                if (!validExpireDate(refreshTokenExpire.value)) {
                    signin()
                    return null
                } else {
                    const result = await http.post("token/refresh", {
                        token: accessToken.value,
                        refreshToken: refreshToken.value
                    })

                    if (result) {
                        setToken(result)
                    }
                }
            }

            return "Bearer " + accessToken.value
        }

        return null
    }

    function isInRole(name) {
        if (roles.value) return roles.value.includes(name)
        return false
    }

    function setProfile(profile, withAuth) {
        if (!profile || profile.authProvider == null) {
            displayName.value = DEFAULT_USER
            language.value = DEFAULT_LANG
            theme.value = DEFAULT_THEME
            canChangePicture.value = false
            roles.value = []
            authProvider.value = DEFAULT_AUTH_PROVIDER
            signedin.value = false
            profilePageUrl.value = ""
            pictureUrl.value = "",
                canChangePassword.value = false
        }
        else {
            displayName.value = profile.displayName
            language.value = profile.language ? profile.language : DEFAULT_LANG
            theme.value = profile.theme ? profile.theme : DEFAULT_THEME
            canChangePicture.value = profile.canChangePicture
            profilePageUrl.value = profile.profilePageUrl
            pictureUrl.value = profile.profileImageUrl || profileImage
            canChangePassword.value = profile.canChangePassword

            if (withAuth) {
                roles.value = profile.roles
                authProvider.value = DEFAULT_AUTH_PROVIDER
                signedin.value = true
            }
        }
    }

    async function getUserData() {
        const result = await http.get('profiles/me')
        setProfile(result, true)
    }

    async function getMainMenus() {
        return await http.get('mainmenus/me')
    }

    async function getSideMenus() {
        return await http.get('sidemenus/me')
    }

    async function localLogin({ email, password }) {
        const result = await http.post('account/signin', { email, password })

        if (result.success) {
            setToken(result.token)

            await getUserData()

            startHub()

            if (redirectUrl.value && redirectUrl.value != '/signin') {
                router.replace(redirectUrl.value)
            } else {
                router.replace('/')
            }
        }

        return result
    }

    function externalLogin(vm, name, success, error) {
        const url = process.env.VUE_APP_SERVER + "api/externallogin/" + name + "/signin"
        browser.open(url)

        browser.onMessage(async (result) => {
            browser.close()

            const json = JSON.parse(result)

            if (json.success) {
                if (json.redirectUrl) {
                    window.location = json.redirectUrl
                    success()
                    return
                }

                setToken(json.token)
                await getUserData()
                startHub()

                if (redirectUrl.value && redirectUrl.value != '/signin') {
                    router.replace(redirectUrl.value)
                } else {
                    router.replace('/')
                }

                success()
            } else if (json.code == CLIENT_ERROR.REDIRECT) {
                vm.$router.replace(json.redirectUrl)
            } else {
                error(json.code)
            }
        })

        browser.onClose((_) => {
            success()
        })
    }

    async function signout() {
        const result = await http.post('account/signout')

        setToken(null)
        setProfile(null, false)

        if (process.env.VUE_APP_HUB_ENABLE == 'true') {
            const notificationStore = useNotificationStore()
            notificationStore.stopHub()
        }

        if (result.redirectUrl != null) {
            window.location = result.redirectUrl
            return
        }

        router.replace('/signout')
    }

    async function updateProfile(profile) {
        await http.put('profiles/me', profile)
        setProfile(profile, false)
    }

    function refreshProfilePicture(newPictureUrl) {
        pictureUrl.value = newPictureUrl
    }

    const appStore = useAppStore()

    const homePage = computed(() => {
        if (overrideHomepage.value != '') return overrideHomepage.value

        if (appStore.config.homePageRoute) return appStore.config.homePageRoute

        if (signedin.value) {
            return appStore.config.userHomePage
                ? "/pages/" + appStore.config.userHomePage
                : "/default"
        }

        return appStore.config.homePage
            ? "/pages/" + appStore.config.homePage
            : "/default"
    })

    const showProfile = computed(() => signedin.value && appStore.config.showProfile)
    const showSearch = computed(() => (signedin.value && appStore.config.showSearch) || appStore.config.alwaysShowSearch)

    async function sendPasswordReset(emailAddress, recaptchaResponse) {
        return await http.post('account/password_reset', { emailAddress, recaptchaResponse })
    }

    async function passwordReset(resetKey, newPassword) {
        return await http.post('account/password_reset/commit/' + resetKey, { newPassword })
    }

    async function register(email, password, recaptchaResponse) {
        return await http.post('account/register', { email, password, recaptchaResponse })
    }

    async function changePassword(oldPassword, newPassword) {
        return await http.post('account/change_password', { oldPassword, newPassword })
    }

    return {
        redirectUrl,
        displayName,
        language,
        theme,
        pictureUrl,
        canChangePicture,
        signedin,
        authProvider,
        accessToken,
        tokenExpire,
        refreshToken,
        refreshTokenExpire,
        roles,
        locale,
        homePage,
        showProfile,
        showSearch,
        profilePageUrl,
        canChangePassword,
        overrideHomepage,

        init,
        getAccessToken,
        getUserData,
        getMainMenus,
        getSideMenus,
        localLogin,
        externalLogin,
        signin,
        signout,
        updateProfile,
        isInRole,
        refreshProfilePicture,
        sendPasswordReset,
        register,
        changePassword,
        passwordReset
    }
}

export const useUserStore = defineStore('user', setup)
